import { Input } from "@getgreenline/blaze-ui";
import { cn } from "@getgreenline/blaze-ui";
import { useFormikContext } from "formik";
import { formatOptions } from "@/app-config";
import { AdFormatCard } from "../cards/ad-format-card";
import { type CampaignFormValues } from "../campaign-form";
import { CampaignContextPill } from "../../shared/campaign-context-pill";
import { FormFieldLabel } from "../../shared/form-field-label";
import { FormTip } from "../../shared/form-tip";
import { StepAlert } from "../../shared/step-alert";
import { StepHeader } from "../../shared/step-header";

type AdFormatStepProps = {
  errorMessage?: string;
  errorField?: string;
  showError?: boolean;
  onDismissError: () => void;
};

export function AdFormatStep({
  errorMessage,
  errorField,
  showError = false,
  onDismissError,
}: AdFormatStepProps) {
  const { values, setFieldValue } = useFormikContext<CampaignFormValues>();
  const selectedBrand = values.brand;

  return (
    <section className="tw:max-w-4xl">
      <StepHeader
        title="Ad format"
        description="Choose how your products will appear on Blaze-powered dispensary menus. Additional formats are on the roadmap."
      />

      {selectedBrand ? (
        <CampaignContextPill
          brandName={selectedBrand.name}
          className="tw:mt-4"
        />
      ) : null}

      <StepAlert
        show
        tone="info"
        message="All ads run exclusively on 21+ verified, compliant Blaze-powered menus. No external placements."
      />

      <StepAlert
        show={showError}
        message={errorMessage}
        onDismiss={onDismissError}
      />
      <div
        className={cn(
          "tw:mt-6 tw:grid tw:grid-cols-3 tw:gap-4",
          showError &&
            errorField === "adFormat" &&
            "tw:rounded-xl tw:border tw:border-destructive/40 tw:bg-destructive/5 tw:p-3",
        )}
      >
        {formatOptions.map((option) => {
          return (
            <AdFormatCard
              key={option.id}
              option={option}
              selected={values.adFormat === option.id}
              onSelect={() => setFieldValue("adFormat", option.id)}
            />
          );
        })}
      </div>

      <FormTip>
        Display banner and featured brand are in development, available later
        this year.
      </FormTip>

      <div className="tw:mt-8 tw:max-w-4xl tw:border-t tw:pt-5">
        <FormFieldLabel htmlFor="campaign-name">Campaign name</FormFieldLabel>
        <Input
          id="campaign-name"
          aria-invalid={showError && errorField === "campaignName"}
          className={cn(
            "tw:placeholder:text-muted-foreground/50",
            showError &&
              errorField === "campaignName" &&
              "tw:border-destructive tw:focus-visible:ring-destructive/20",
          )}
          value={values.campaignName}
          onChange={(event) =>
            setFieldValue("campaignName", event.target.value)
          }
          placeholder={
            selectedBrand
              ? `${selectedBrand.name} - Summer push`
              : "Campaign name"
          }
        />
      </div>
    </section>
  );
}
